import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CategoryService } from 'src/app/services/category.service';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import { DiscountCouponsService } from 'src/app/services/discount-coupons.service';
import { AcademyService } from '../../service/academies.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface Week {
  label: string,
  value: string
}

@Component({
  selector: 'app-edit-coupon',
  templateUrl: './edit-coupon.component.html',
  styleUrls: ['./edit-coupon.component.scss'],
  providers: [ConfirmationService, MessageService]
})

export class EditCouponComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  discountCouponsForm: FormGroup;
  errorMessage = '';
  submitted = false;
  pagetitle: string;
  couponDiscountTypes: any = [];
  couponsModules: any = [];
  pushNotificationAppTypes: any = [];
  notificationIconPreview: string | null = null;
  notificationIconFile: File | null = null;
  couponsUsage: any;
  weeks: Week[] = environment.weeks;
  discountType: any;
  yearRange = environment.yearRange;
  academyId: string;
  id: string;
  academiesList: any;
  couponsRefundHours: any[] = [];
  disableCouponHours: boolean[] = [];
  isCouponHourSelected: boolean = false;
  discountCouponData: Object;

  constructor(private discountCouponService: DiscountCouponsService, private formBuilder: FormBuilder, private messageService: MessageService,
    private router: Router, private categoryService: CategoryService, private activatedRoute: ActivatedRoute, private academyService: AcademyService) { }

  async ngOnInit(): Promise<void> {
    this.pagetitle = 'Edit Coupon';
    this.academyId = this.activatedRoute.snapshot.paramMap.get('academyId');
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    this.couponDiscountTypes = [
      { label: 'Percentage (%)', value: 'percentage' },
      { label: 'Flat Amount (₹)', value: 'flat' }
    ];

    this.discountCouponsForm = this.formBuilder.group({
      module: [null, [Validators.required]],
      name: [null, [Validators.required]],
      type: [null, [Validators.required]],
      value: [null, [Validators.required]],
      description: [null, [Validators.required]],
      code: [null, [Validators.required]],
      academyId: [null],
      termsAndCondition: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      usageId: [null, [Validators.required]],
      noOfTimes: [{ value: null, disabled: true }],
      availability: this.formBuilder.array([]),
      refund: this.formBuilder.array([
        this.refundFormGroup
      ])
    });

    this.weeks.forEach(week => {
      const availabilityFormGroup = this.formBuilder.group({
        active: [false],
        day: [week.label.slice(0, 3)],
        from: [null],
        to: [{ value: null, disabled: true }],
        id: [null]
      })
      this.availabilityFormArray.push(availabilityFormGroup);
    })

    this.getCouponModules();
    this.getCouponRefundHours();
    this.getCouponUsageDetails();
    if (!this.academyId) {
      this.getAcademyList();
      this.discountCouponsForm.get('academyId').setValidators([Validators.required]);
      this.discountCouponsForm.get('academyId').updateValueAndValidity();
    } else {
      this.discountCouponsForm.get('academyId').setValue(this.academyId);
    }
    setTimeout(() => {
      this.getDiscountCouponsDetailsById(this.id);
    }, 1000);
  }

  getAcademyList() {
    let query = "?filterByDisable=false";
    this.academyService.getAcademyList(query).pipe(takeUntil(this.destroy$)).subscribe(
      (data) => {
        this.academiesList = data.data.items;
      });
  }

  getDiscountCouponsDetailsById(id: string) {
    this.discountCouponService.getDiscountCouponById(id).pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.discountCouponData = data;
      this.onSelectDiscountType({ value: { value: data['type'] } });

      this.discountCouponsForm.patchValue({
        module: this.couponsModules.find(ele => ele.id === data['moduleId']),
        name: data['name'],
        type: this.couponDiscountTypes.find(ele => ele.value === data['type']),
        value: data['value'],
        description: data['description'],
        code: data['code'],
        academyId: data['academyId'],
        termsAndCondition: data['termsAndCondition'],
        startDate: moment(data['startDate']).toDate(),
        endDate: moment(data['endDate']).toDate(),
        usageId: this.couponsUsage.find(ele => ele.id === data['usageData'][0]['_id']),
        noOfTimes: data['noOfTimes'],
      })

      this.onSelectCouponUsage({ value: { name: data['usageData'][0]['name'] } });

      data['availability'].forEach((ele, index) => {
        this.availabilityFormArray.controls[index]['controls']['id'].setValue(ele['_id']);
        this.availabilityFormArray.controls[index]['controls']['active'].setValue(ele['active']);
        if (ele['from']) {
          const fromTime = moment(ele['from'], 'hh:mm A').toDate();
          this.onSelectTime(fromTime, 'from', index);
        }
        if (ele['to']) {
          const toTime = moment(ele['to'], 'hh:mm A').toDate();
          this.onSelectTime(toTime, 'to', index);
        }
      })
      data['refund'].forEach((ele, index) => {
        this.refundFormArray.controls[this.refundFormArray.length - 1]['controls']['hours'].setValue(this.couponsRefundHours.find(coupon => coupon.id === ele.hours));
        this.refundFormArray.controls[this.refundFormArray.length - 1]['controls']['percentage'].setValue(ele.percentage);
        this.refundFormArray.controls[this.refundFormArray.length - 1]['controls']['id'].setValue(ele._id);
        this.onSelectCouponHour({ value: this.couponsRefundHours.find(coupon => coupon.id === ele.hours)});
        if ((data['refund'].length - 1) !== index) {
          this.addRefund();
        }
      })
    },
      ((err) => {
        this.messageService.add({ key: 'toastmsg', severity: 'error', summary: err.error.message, detail: 'Fetching Discount Coupon details failed', life: 6000 });
      }))
  }

  get availabilityFormArray(): FormArray {
    return this.discountCouponsForm.get('availability') as FormArray;
  }

  get refundFormArray(): FormArray {
    return this.discountCouponsForm.get('refund') as FormArray;
  }

  get refundFormGroup(): FormGroup {
    return this.formBuilder.group({
      id: [null],
      hours: [null, [Validators.required]],
      percentage: [null, [Validators.required, Validators.min(1), Validators.max(100)]]
    })
  }

  addRefund() {
    let couponHourSelected = this.refundFormArray.controls[this.refundFormArray.length - 1]['controls']['hours'].value;
    if (couponHourSelected) {
      let couponsRefundHour = this.couponsRefundHours.find(ele => ele.id === couponHourSelected.id);
      couponsRefundHour['inactive'] = true;
      this.disableCouponHours[this.refundFormArray.length - 1] = true;
    }
    this.refundFormArray.push(this.refundFormGroup);
    this.disableCouponHours.push(false);
    this.isCouponHourSelected = false;
  }

  removeRefund(i: number) {
    let couponHourSelected = this.refundFormArray.controls[i]['controls']['hours'].value;
    if (!couponHourSelected) {
      couponHourSelected = this.refundFormArray.controls[i - 1]['controls']['hours'].value;
    }
    let couponsRefundHour = this.couponsRefundHours.find(ele => ele.id === couponHourSelected.id);
    couponsRefundHour['inactive'] = false;
    this.refundFormArray.removeAt(i);
    this.disableCouponHours.splice(i, 1);
    this.disableCouponHours[this.refundFormArray.length - 1] = false;
    this.isCouponHourSelected = this.refundFormArray.value.every(ele => ele.hours !== null);
  }

  onSelectCouponHour(event) {
    const selectedCouponHourId = event.value.id;
    this.isCouponHourSelected = (!selectedCouponHourId) ? false : true;
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.discountCouponsForm.controls;
  }

  h(controlName: string, i: number) {
    return this.discountCouponsForm.controls[controlName]['controls'][i]['controls'];
  }

  getCouponModules() {
    const query = `?filterByParentCategory=couponmodule`;
    this.categoryService.getCategoryList(query).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.couponsModules = data?.data?.items;
      if (this.academyId !== null) {
        this.discountCouponsForm.get('module').setValue(this.couponsModules.find(ele => ele.name === 'Book a court'));
        this.discountCouponsForm.get('module').disable();
      }
    })
  }

  getCouponRefundHours() {
    const query = `?filterByParentCategory=couponRefundHours`;
    this.categoryService.getCategoryList(query).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.couponsRefundHours = data?.data?.items;
      this.couponsRefundHours.forEach(ele => {
        ele['inactive'] = false;
      })
    })
  }

  getCouponUsageDetails() {
    const query = `?filterByParentCategory=couponusage`;
    this.categoryService.getCategoryList(query).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.couponsUsage = data?.data?.items;
    })
  }

  onSelectTime(event: any, time: string, index: number) {
    const currentTime = new Date(event);
    currentTime.setMinutes(0, 0, 0);
    this.discountCouponsForm.get('availability')['controls'][index]['controls'][time].setValue(currentTime);
    this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].setValidators([Validators.required]);
    this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].updateValueAndValidity();
    this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].enable();
    this.setTimeValidations(time, index);
  }


  setTimeValidations(dayTime, index) {
    const startTime = this.discountCouponsForm.get('availability')['controls'][index]['controls']['from'].value;
    const endTime = this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].value;
    if (startTime && endTime) {
      if (dayTime === 'startTime') {
        this.discountCouponsForm.get('availability')['controls'][index]['controls'][dayTime].setErrors((startTime.getHours() >= endTime.getHours()) ? { startTimeValidator: true } : null);
      } else {
        this.discountCouponsForm.get('availability')['controls'][index]['controls'][dayTime].setErrors((endTime.getHours() <= startTime.getHours()) ? { endTimeValidator: true } : null);
      }
    }
  }


  onSelectDiscountType(event) {
    this.discountType = event.value.value;
    this.discountCouponsForm.get('value').setValue(null);
    this.discountCouponsForm.get('value').setValidators((event.value.value === 'percentage') ? [Validators.required, Validators.min(1), Validators.max(100)] : [Validators.required, Validators.min(1)]);
    this.discountCouponsForm.get('value').updateValueAndValidity();
  }

  onSelectCouponUsage(event) {
    if (event.value.name === 'limited') {
      this.discountCouponsForm.get('noOfTimes').enable();
      this.discountCouponsForm.get('noOfTimes').setValidators([Validators.required]);
      this.discountCouponsForm.get('noOfTimes').updateValueAndValidity();
    } else {
      this.discountCouponsForm.get('noOfTimes').reset();
      this.discountCouponsForm.get('noOfTimes').disable();
    }
  }

  onSubmit() {
    this.submitted = true;
    if (this.discountCouponsForm.invalid) {
      return;
    }
    this.discountCouponsForm.enable();

    var discountCouponsFormData = JSON.parse(JSON.stringify(this.discountCouponsForm.value));
    discountCouponsFormData['module'] = discountCouponsFormData['module']['id'];
    discountCouponsFormData['type'] = discountCouponsFormData['type']['value'];
    discountCouponsFormData['startDate'] = moment(discountCouponsFormData['startDate']).format('yyyy-MM-DD');
    discountCouponsFormData['endDate'] = moment(discountCouponsFormData['endDate']).format('yyyy-MM-DD');
    discountCouponsFormData['usageId'] = discountCouponsFormData['usageId']['id'];
    discountCouponsFormData['availability'].forEach(ele => {
      if (ele['from'] !== null) {
        ele['from'] = moment(ele['from']).format('hh:mm A');
      }
      if (ele['to'] !== null) {
        ele['to'] = moment(ele['to']).format('hh:mm A');
      }
    });
    discountCouponsFormData['refund'].forEach(ele => {
      if (ele['hours']) {
        ele['hours'] = ele['hours']['id']
      }
    });

    discountCouponsFormData = JSON.stringify(discountCouponsFormData, null, 4);
    this.updateDiscountCoupons(discountCouponsFormData);
  }

  updateDiscountCoupons(discountCouponsFormData) {
    this.discountCouponService.updateDiscountCoupon(this.id, discountCouponsFormData).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.messageService.add({ key: 'toastmsg', severity: 'success', summary: 'Successful', detail: 'Discount Coupon Updated Successfully!!', life: 6000 });
        setTimeout(() => {
          if (this.academyId) {
            this.router.navigate(['/manage/academies/discount-coupons/' + this.academyId]);
          } else {
            this.router.navigate(['/manage/discount-coupons']);
          }
        }, 2000);
      },
      ((err) => {
        this.messageService.add({ key: 'toastmsg', severity: 'error', summary: err.error.message, detail: 'Update Discount Coupon failed', life: 6000 });
      })
    );
  }

  onReset() {
    this.submitted = false;
    this.discountCouponsForm.reset();
  }

  backLink() {
    if (this.academyId) {
      this.router.navigate(['/manage/academies/discount-coupons/' + this.academyId]);
    } else {
      this.router.navigate(['/manage/discount-coupons']);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
